import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../components/AdminSidebar";
import AOS from "aos";
import "aos/dist/aos.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";

const AdminProductPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    stock: "",
    image: "",
    categoryId: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [theme, setTheme] = useState("light");
  const navigate = useNavigate();

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }

    fetchProducts();
    fetchCategories();
    AOS.init();
  }, [token, navigate]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/products`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      localStorage.removeItem("token");
      if (err.response?.status === 403) navigate("/admin/login");
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/categories`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.log("Failed to load categories");
      setCategories([]);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/products/${editingId}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/products`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setForm({
        name: "",
        description: "",
        price: "",
        stock: "",
        image: "",
        categoryId: "",
      });
      setEditingId(null);
      fetchProducts();
    } catch (err) {
      alert("Error saving product");
    }
  };

  const handleEdit = (prod) => {
    setForm(prod);
    setEditingId(prod.id);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this product?")) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/products/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchProducts();
      } catch (err) {
        alert("Delete failed");
      }
    }
  };

  const filteredProducts = Array.isArray(products)
    ? products.filter((prod) =>
        prod.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const toggleTheme = () => setTheme(theme === "light" ? "dark" : "light");

  return (
    <AdminSidebar theme={theme}>
      <div
        className={`container-fluid p-4 ${
          theme === "dark" ? "bg-dark text-white" : "bg-light"
        }`}
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="h3">Manage Products</h1>
          <button
            className="btn btn-outline-secondary"
            onClick={toggleTheme}
          >
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
        </div>

        {/* --- Product Form --- */}
        <form
          onSubmit={handleSubmit}
          className="row g-3 mb-5"
          data-aos="fade-up"
        >
          <div className="col-md-6">
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Name"
              className="form-control"
              required
            />
          </div>
          <div className="col-md-6">
            <input
              type="number"
              name="price"
              value={form.price}
              onChange={handleChange}
              placeholder="Price"
              className="form-control"
              required
            />
          </div>
          <div className="col-md-6">
            <input
              type="number"
              name="stock"
              value={form.stock}
              onChange={handleChange}
              placeholder="Stock"
              className="form-control"
              required
            />
          </div>
          <div className="col-md-6">
            <input
              type="text"
              name="image"
              value={form.image}
              onChange={handleChange}
              placeholder="Image URL"
              className="form-control"
            />
          </div>
          <div className="col-md-6">
            <select
              name="categoryId"
              value={form.categoryId}
              onChange={handleChange}
              className="form-select"
              required
            >
              <option value="">Select Category</option>
              {Array.isArray(categories) &&
                categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="col-12">
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Description"
              className="form-control"
            />
          </div>
          <div className="col-12">
            <button type="submit" className="btn btn-primary w-100">
              {editingId ? "Update" : "Add"} Product
            </button>
          </div>
        </form>

        {/* --- Product List --- */}
        <div data-aos="fade-up">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="h5">Product List</h2>
            <input
              type="text"
              placeholder="Search products..."
              className="form-control w-25"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {filteredProducts.length === 0 ? (
            <p>No products found.</p>
          ) : (
            <div className="accordion" id="productAccordion">
              {filteredProducts.map((prod, index) => (
                <div className="accordion-item" key={prod.id}>
                  <h2 className="accordion-header" id={`heading${index}`}>
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#collapse${index}`}
                      aria-expanded="false"
                      aria-controls={`collapse${index}`}
                    >
                      {prod.name} (₹{prod.price})
                    </button>
                  </h2>
                  <div
                    id={`collapse${index}`}
                    className="accordion-collapse collapse"
                    aria-labelledby={`heading${index}`}
                    data-bs-parent="#productAccordion"
                  >
                    <div className="accordion-body">
                      <div className="d-flex gap-4 align-items-start">
                        {prod.image && (
                          <img
                            src={prod.image}
                            alt={prod.name}
                            className="img-thumbnail"
                            style={{ width: "120px" }}
                          />
                        )}
                        <div>
                          <p>
                            <strong>Description:</strong> {prod.description}
                          </p>
                          <p>
                            <strong>Stock:</strong> {prod.stock}
                          </p>
                          <p>
                            <strong>Category:</strong>{" "}
                            {categories.find(
                              (c) => c.id === prod.categoryId
                            )?.name || "Unknown"}
                          </p>
                          <p>
                            <strong>Price:</strong> ₹{prod.price}
                            {prod.discountPercent > 0 && (
                              <span style={{ color: "red" }}>
                                {" "}
                                (Discount: {prod.discountPercent}% → ₹
                                {prod.discountedPrice})
                              </span>
                            )}
                          </p>
                          <div className="mt-3">
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEdit(prod)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(prod.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminSidebar>
  );
};

export default AdminProductPage;
